'use client';

import React from 'react';

// ============================================================================
// Progress Context for compound component communication
// ============================================================================

interface ProgressContextValue {
  value: number;
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const ProgressContext = React.createContext<ProgressContextValue | null>(null);

const useProgressContext = () => {
  const context = React.useContext(ProgressContext);
  if (!context) {
    throw new Error('Progress compound components must be used within Progress.Root');
  }
  return context;
};

// ============================================================================
// Size and Color configurations (no Tailwind CSS dependency)
// ============================================================================

const PROGRESS_SIZES = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
} as const;

const PROGRESS_COLORS = {
  blue: '#2563EB',
  green: '#16A34A',
  red: '#DC2626',
  yellow: '#CA8A04',
  purple: '#9333EA',
  gray: '#4B5563',
} as const;

// Progress Root component
export interface ProgressRootProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The progress value (0-100)
   */
  value?: number;
  /**
   * The size of the progress bar
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * The label for screen readers
   */
  label?: string;
  children?: React.ReactNode;
}

const ProgressRoot = React.forwardRef<HTMLDivElement, ProgressRootProps>(
  ({ className, size = 'md', value = 0, label = 'Progress', children, ...props }, ref) => {
    const clampedValue = Math.min(100, Math.max(0, value));

    return (
      <ProgressContext.Provider value={{ value: clampedValue, size }}>
        <div
          ref={ref}
          className={className}
          role="progressbar"
          aria-label={label}
          aria-valuenow={clampedValue}
          aria-valuemin={0}
          aria-valuemax={100}
          data-value={clampedValue}
          {...props}
        >
          {children}
        </div>
      </ProgressContext.Provider>
    );
  }
);

ProgressRoot.displayName = 'ProgressRoot';

// Progress Track component
export interface ProgressTrackProps extends React.HTMLAttributes<HTMLDivElement> {
  backgroundColor?: string;
  borderRadius?: string;
}

const ProgressTrack = React.forwardRef<HTMLDivElement, ProgressTrackProps>(
  ({ style, backgroundColor, borderRadius, children, ...props }, ref) => {
    const computedStyle: React.CSSProperties = {
      position: 'relative',
      width: '100%',
      overflow: 'hidden',
      backgroundColor: backgroundColor || '#E5E7EB',
      borderRadius: borderRadius || '0.375rem',
      height: '0.5rem',
      ...style,
    };

    return (
      <div ref={ref} style={computedStyle} {...props}>
        {children}
      </div>
    );
  }
);

ProgressTrack.displayName = 'ProgressTrack';

// Progress Range component
export interface ProgressRangeProps extends React.HTMLAttributes<HTMLDivElement> {
  backgroundColor?: string;
}

const ProgressRange = React.forwardRef<HTMLDivElement, ProgressRangeProps>(
  ({ style, backgroundColor, ...props }, ref) => {
    const { value } = useProgressContext();

    const rangeStyle: React.CSSProperties = {
      height: '100%',
      transition: 'all 300ms ease-in-out',
      width: `${value}%`,
      backgroundColor: backgroundColor || '#0066CC',
      borderRadius: 'inherit',
      ...style,
    };

    return <div ref={ref} style={rangeStyle} {...props} />;
  }
);

ProgressRange.displayName = 'ProgressRange';

// Legacy Progress component for backward compatibility
export interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The progress value (0-100)
   */
  value?: number;
  /**
   * The size of the progress bar
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * The color of the progress bar
   */
  color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'gray' | string;
  /**
   * Whether to show stripe animation
   */
  isStripe?: boolean;
  /**
   * Whether the stripe should be animated
   */
  isAnimated?: boolean;
  /**
   * The label for screen readers
   */
  label?: string;
}

// Type for compound components
export interface ProgressCompoundComponents {
  Root: typeof ProgressRoot;
  Track: typeof ProgressTrack;
  Range: typeof ProgressRange;
}

const ProgressComponent = React.forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      size = 'md',
      value = 0,
      color = 'blue',
      isStripe = false,
      isAnimated: _isAnimated = false,
      label = 'Progress',
      style,
      ...props
    },
    ref
  ) => {
    const clampedValue = Math.min(100, Math.max(0, value));
    const isColorVariant =
      typeof color === 'string' &&
      ['blue', 'green', 'red', 'yellow', 'purple', 'gray'].includes(color);

    const trackStyle: React.CSSProperties = {
      position: 'relative',
      width: '100%',
      overflow: 'hidden',
      borderRadius: '9999px',
      backgroundColor: '#E5E7EB',
      height: PROGRESS_SIZES[size] || PROGRESS_SIZES.md,
      ...style,
    };

    const barColor = isColorVariant
      ? PROGRESS_COLORS[color as keyof typeof PROGRESS_COLORS]
      : color;

    const barStyle: React.CSSProperties = {
      height: '100%',
      borderRadius: '9999px',
      transition: 'all 300ms ease-in-out',
      width: `${clampedValue}%`,
      backgroundColor: barColor,
      ...(isStripe
        ? {
            backgroundImage: `linear-gradient(
              45deg,
              rgba(255, 255, 255, 0.15) 25%,
              transparent 25%,
              transparent 50%,
              rgba(255, 255, 255, 0.15) 50%,
              rgba(255, 255, 255, 0.15) 75%,
              transparent 75%,
              transparent
            )`,
            backgroundSize: '1rem 1rem',
          }
        : {}),
    };

    return (
      <div
        ref={ref}
        style={trackStyle}
        role="progressbar"
        aria-label={label}
        aria-valuenow={clampedValue}
        aria-valuemin={0}
        aria-valuemax={100}
        {...props}
      >
        <div style={barStyle} />
      </div>
    );
  }
);

ProgressComponent.displayName = 'Progress';

// Create compound component namespace
export const ProgressNamespace = {
  Root: ProgressRoot,
  Track: ProgressTrack,
  Range: ProgressRange,
};

// Type for the exported Progress with compound components
type ProgressWithCompounds = typeof ProgressComponent & ProgressCompoundComponents;

// Create the final Progress export with compound components
export const Progress = Object.assign(ProgressComponent, {
  Root: ProgressRoot,
  Track: ProgressTrack,
  Range: ProgressRange,
}) as ProgressWithCompounds;
